import React, { useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import type { DefaultValues } from 'react-hook-form';
import { ButtonAdd } from '../../../components/Buttons';
import { FormContainer, FormRow } from '../../../components/form';
import { TranslationTable } from './TranslationTable';
import './../Translations.css';

type TranslationColumn = {
    field: string;
    header: string;
    required: boolean;
    inputType?: 'text' | 'textarea';
    rows?: number;
};

type IdentityColumn = {
    field: string;
    header: string;
};

interface EntityTranslationsFormProps<TResponse, TPayload> {
    translations: TResponse | null;
    itemsFieldName: keyof TResponse & string;
    translationColumns: TranslationColumn[];
    identityColumns: IdentityColumn[];
    processing: boolean;
    onSubmit: (data: TPayload) => Promise<void> | void;
    buildPayload: (data: TResponse) => TPayload;
}

export function LanguageEntityTranslationsForm <TResponse extends Record<string, any>, TPayload>({
    translations,
    itemsFieldName,
    translationColumns,
    identityColumns,
    processing,
    onSubmit,
    buildPayload
}: EntityTranslationsFormProps<TResponse, TPayload>) {
    const defaultValues = useMemo<DefaultValues<TResponse>>(() => {
        return (translations ?? { [itemsFieldName]: [] }) as DefaultValues<TResponse>;
    }, [translations, itemsFieldName]);

    const { control, handleSubmit, reset } = useForm<TResponse>({
        defaultValues,
        disabled: processing
    });

    useEffect(() => {
        if (translations) {
            reset(defaultValues);
        }
    }, [translations, reset, defaultValues]);

    const { fields } = useFieldArray({
        control,
        name: itemsFieldName as any
    });

    return (
        <form onSubmit={handleSubmit((data) => onSubmit(buildPayload(data)))}>
            <FormContainer>
                <div className='translation-content'>
                    <TranslationTable
                        fields={fields}
                        control={control}
                        arrayName={itemsFieldName}
                        identityColumns={identityColumns}
                        translationColumns={translationColumns}
                        variant='entity'
                    />

                    <FormRow type='button'>
                        <div className='translation-actions flex-right w-full mt-4'>
                            <ButtonAdd
                                label='Uložit překlady'
                                type='submit'
                                disabled={processing}
                                loading={processing}
                            />
                        </div>
                    </FormRow>
                </div>
            </FormContainer>
        </form>
    );
};
